import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Autocomplete, AutocompleteItem } from "@heroui/react";
import { useState, useEffect } from "react";
import { formatCurrency } from "shared/utils/currency";
import { Plus, Trash, Link } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { RenovationProject } from "shared/types/renovation";
export interface SplitInvoiceLine {
  /** Project this part of the invoice is assigned to. */
  projectId: number;
  partId: number;
  amount: number;
  amount_untaxed: number;
  description?: string;
}

interface SplitInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: any;
  /** Project pre-selected for the invoice; used as the default for the first line. */
  selectedProjectId: number;
  availableProjects: RenovationProject[];
  /** Returns the available project parts for a given project (building-scoped). */
  getPartsForProject: (projectId: number) => any[];
  onSave: (splitLines: SplitInvoiceLine[]) => void;
}

// Add these utility functions for number formatting
const formatNumberWithThousandsSeparator = (value: number): string => {
  return value.toLocaleString('nl-NL', { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  });
};

const parseFormattedNumber = (value: string, isEditing: boolean): number => {
  // Remove any whitespace
  let cleanValue = value.replace(/\s/g, '');
  
  // Handle empty or invalid input
  if (!cleanValue || cleanValue === '-' || cleanValue === '.') {
    return 0;
  }

  // If we're currently editing (has focus), treat all dots as thousands separators
  if (isEditing) {
    // Remove all dots and replace comma with dot for parsing
    return parseFloat(cleanValue.replace(/\./g, '').replace(',', '.')) || 0;
  }

  // Handle leading zeros
  if (cleanValue.startsWith('0') && cleanValue.length > 1 && !cleanValue.startsWith('0.')) {
    cleanValue = cleanValue.substring(1);
  }

  // If the value ends with a decimal separator, treat it as a partial input
  if (cleanValue.endsWith('.') || cleanValue.endsWith(',')) {
    return parseFloat(cleanValue.slice(0, -1).replace(',', '.')) || 0;
  }

  // If there's only one separator, treat it as a decimal
  if ((cleanValue.match(/\./g) || []).length === 1) {
    return parseFloat(cleanValue.replace(',', '')) || 0;
  }
  if ((cleanValue.match(/,/g) || []).length === 1) {
    return parseFloat(cleanValue.replace('.', '').replace(',', '.')) || 0;
  }

  // If multiple separators, determine which is decimal based on position
  if (cleanValue.includes('.') && cleanValue.includes(',')) {
    const lastDot = cleanValue.lastIndexOf('.');
    const lastComma = cleanValue.lastIndexOf(',');
    if (lastDot > lastComma) {
      // Period is decimal
      return parseFloat(cleanValue.replace(/,/g, '')) || 0;
    } else {
      // Comma is decimal
      return parseFloat(cleanValue.replace(/\./g, '').replace(',', '.')) || 0;
    }
  }

  // If no separators, just parse as is
  return parseFloat(cleanValue) || 0;
};

export const SplitInvoiceModal = ({
  isOpen,
  onClose,
  invoice,
  selectedProjectId,
  availableProjects,
  getPartsForProject,
  onSave
}: SplitInvoiceModalProps) => {
  const [splitLines, setSplitLines] = useState<SplitInvoiceLine[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  
  // Add state to track which inputs are being edited
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  
  // Add state to track raw input values during editing
  const [inputValues, setInputValues] = useState<Record<number, string>>({});
  
  // Initialize when modal opens with invoice data
  useEffect(() => {
    if (!isOpen || !invoice) return;
    
    // Use absolute values for the UI so users work with positive numbers
    const totalInvoiceAmount = Math.abs(invoice.amount || 0);
    const totalUntaxed = Math.abs(invoice.amount_untaxed || totalInvoiceAmount);
    
    // Round to 2 decimal places
    const roundedAmount = Math.round(totalInvoiceAmount * 100) / 100;
    const roundedUntaxed = Math.round(totalUntaxed * 100) / 100;
    
    setTotalAmount(roundedAmount);
    
    // Always reset split lines when opening for a new invoice
    setSplitLines([{
      projectId: selectedProjectId || 0, // Default to the pre-selected project
      partId: 0, // No part selected initially
      amount: roundedAmount,
      amount_untaxed: roundedUntaxed,
      description: invoice.name || '' // Initialize with invoice name
    }]);
    setRemainingAmount(0); // All allocated

    // Set initial input value with proper formatting
    setInputValues({
      0: formatNumberWithThousandsSeparator(roundedAmount)
    });
  }, [isOpen, invoice?.move_line_id]); // Track move_line_id to detect invoice changes


  const calculateRemaining = () => {
    if (!invoice) return;
    
    const totalInvoiceAmount = Math.abs(invoice.amount || 0);
    const allocatedAmount = splitLines.reduce((sum, line) => {
      // Use the current input value if we're editing this line
      if (editingIndex !== null && editingIndex === splitLines.indexOf(line)) {
        const currentValue = inputValues[editingIndex];
        if (currentValue) {
          return sum + parseFormattedNumber(currentValue, true);
        }
      }
      return sum + (line.amount || 0);
    }, 0);
    
    // Round to 2 decimal places to avoid floating point issues
    const roundedAllocated = Math.round(allocatedAmount * 100) / 100;
    const roundedTotal = Math.round(totalInvoiceAmount * 100) / 100;
    setRemainingAmount(roundedTotal - roundedAllocated);
  };
  
  // Recalculate remaining when split lines change or when editing
  useEffect(() => {
    calculateRemaining();
  }, [splitLines, inputValues, editingIndex]);
  
  const handleAddLine = () => {
    // Add a new line - don't check remaining amount here to allow more flexibility
    setSplitLines([
      ...splitLines,
      {
        projectId: selectedProjectId || 0,
        partId: 0,
        amount: remainingAmount > 0 ? remainingAmount : 0,
        amount_untaxed: remainingAmount > 0 ? remainingAmount : 0
      }
    ]);
  };
  
  const handleRemoveLine = (index: number) => {
    if (splitLines.length <= 1) {
      toast.warn("Er moet minstens één regel zijn");
      return;
    }
    
    const newLines = [...splitLines];
    newLines.splice(index, 1);
    setSplitLines(newLines);
  };
  
  const handleProjectChange = (index: number, projectId: number) => {
    const newLines = [...splitLines];
    // Switching project invalidates the chosen part (parts are project-specific).
    newLines[index] = { ...newLines[index], projectId, partId: 0 };
    setSplitLines(newLines);
  };

  const handlePartChange = (index: number, partId: number) => {
    const newLines = [...splitLines];
    newLines[index].partId = partId;
    setSplitLines(newLines);
  };
  
  const handleAmountChange = (index: number, value: string) => {
    // Store the raw input value
    setInputValues({
      ...inputValues,
      [index]: value
    });
    
    // Allow flexible input with both decimal formats
    if (value && !/^[0-9]*([.,]?[0-9]*)?$/.test(value.replace(/\s/g, ''))) {
      return;
    }
    
    const newLines = [...splitLines];
    
    if (value) {
      // Try to parse the value
      const parsed = parseFormattedNumber(value, true);
      if (!isNaN(parsed)) {
        // Round to 2 decimal places
        const rounded = Math.round(parsed * 100) / 100;
        newLines[index].amount = rounded;
        newLines[index].amount_untaxed = rounded;
        setSplitLines(newLines);
      }
    } else {
      // Handle empty value
      newLines[index].amount = 0;
      newLines[index].amount_untaxed = 0;
      setSplitLines(newLines);
    }
  };
  
  const handleAmountFocus = (index: number) => {
    setEditingIndex(index);
    // Always set the plain value when focusing, regardless of whether we have a value or not
    const currentAmount = splitLines[index].amount;
    // Convert to plain format without any formatting
    const plainValue = currentAmount.toFixed(2).replace('.', ',');
    setInputValues({
      ...inputValues,
      [index]: plainValue
    });
  };
  
  const handleAmountBlur = (index: number) => {
    setEditingIndex(null);
    
    // Parse the final input and update the value
    const value = inputValues[index] || '0';
    const numericValue = parseFormattedNumber(value, false);
    
    // Round to 2 decimal places
    const roundedValue = Math.round(numericValue * 100) / 100;
    
    const newLines = [...splitLines];
    newLines[index].amount = roundedValue;
    newLines[index].amount_untaxed = roundedValue;
    setSplitLines(newLines);
    
    // Format to Dutch locale on blur
    setInputValues({
      ...inputValues,
      [index]: formatNumberWithThousandsSeparator(roundedValue)
    });
  };
  
  const getDisplayAmount = (index: number, amount: number): string => {
    // If currently editing this field, show raw input value
    if (editingIndex === index) {
      return inputValues[index] || '';
    }
    
    // Otherwise show formatted value with thousands separators
    return formatNumberWithThousandsSeparator(amount);
  };
  
  const handleSave = () => {
    // Validate all lines have a project selected
    const linesWithoutProject = splitLines.filter(line => !line.projectId);
    if (linesWithoutProject.length > 0) {
      toast.error(`Selecteer een project voor ${linesWithoutProject.length === 1 ? 'de' : 'alle'} regel${linesWithoutProject.length === 1 ? '' : 's'}`);
      return;
    }

    // Validate all lines have a part selected
    const incompleteLines = splitLines.filter(line => line.partId === 0);
    if (incompleteLines.length > 0) {
      toast.error(`Selecteer een projectonderdeel voor ${incompleteLines.length === 1 ? 'de' : 'alle'} regel${incompleteLines.length === 1 ? '' : 's'}`);
      return;
    }
    
    // Validate all lines have a description
    const linesWithoutDescription = splitLines.filter(line => !line.description || line.description.trim() === '');
    if (linesWithoutDescription.length > 0) {
      toast.error(`Vul een omschrijving in voor ${linesWithoutDescription.length === 1 ? 'de' : 'alle'} regel${linesWithoutDescription.length === 1 ? '' : 's'}`);
      return;
    }
    
    // Validate amounts sum to total
    const totalAllocated = splitLines.reduce((sum, line) => sum + (line.amount || 0), 0);
    const difference = Math.abs(totalAllocated - totalAmount);
    
    if (difference > 0.01) { // Allow small rounding differences
      toast.error(`Totaal verdeeld bedrag (${formatCurrency(totalAllocated)}) moet gelijk zijn aan factuur bedrag (${formatCurrency(totalAmount)})`);
      return;
    }
    
    // Convert to negative values for saving (since invoice amounts are negative)
    const negatedLines = splitLines.map(line => ({
      ...line,
      amount: -Math.abs(line.amount),
      amount_untaxed: -Math.abs(line.amount_untaxed)
    }));
    
    onSave(negatedLines);
    onClose();
  };
  
  // Use a native click handler with stopPropagation to prevent HeroUI modal
  // focus management from interfering. setTimeout decouples window.open from
  // the event chain so the modal doesn't detect a focus loss.
  const handleOpenPDF = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (invoice?.invoice_pdf?.data) {
      try {
        const base64Data = invoice.invoice_pdf.data;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        setTimeout(() => {
          window.open(url, '_blank', 'noopener,noreferrer');
          // Clean up after enough time for the new tab to load the PDF
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }, 0);
      } catch (error) {
        console.error('Error opening PDF:', error);
        toast.error('Fout bij openen van PDF');
      }
    } else {
      toast.warn('PDF niet beschikbaar');
    }
  };
  
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <div className="text-xl">Verdeel factuur over projecten en onderdelen</div>
        </ModalHeader>
        
        <ModalBody>
          <div className="flex flex-col gap-4">
            {/* Invoice details */}
            <div className="bg-neutral-100 p-4 rounded-lg">
              <div className="flex justify-between mb-2">
                <div className="font-medium">Factuur details</div>
                <button
                  type="button"
                  onClick={handleOpenPDF}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-moss-primary text-white rounded-medium hover:opacity-90"
                >
                  <Link size={14} />
                  Open PDF
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-neutral-500">Leverancier:</span>
                  <span className="ml-2 font-medium">{invoice?.partner_id[1]}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Datum:</span>
                  <span className="ml-2">{invoice?.date}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Factuur:</span>
                  <span className="ml-2">{invoice?.name}</span>
                </div>
                <div>
                  <span className="text-neutral-500">Bedrag:</span>
                  <span className="ml-2 font-medium">{formatCurrency(Math.abs(invoice?.amount))}</span>
                </div>
              </div>
            </div>
          
            
            {/* Split Lines */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium">Factuur verdeling</div>
                <div className="text-sm">
                  <span className="text-neutral-500">Nog te verdelen:</span>
                  <span className={`ml-1 font-medium ${remainingAmount > 0 ? 'text-brick-primary' : (remainingAmount < 0 ? 'text-red-500' : 'text-status-good')}`}>
                    {formatCurrency(remainingAmount)}
                  </span>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-neutral-50">
                      <th className="text-left p-2 border-b">Project</th>
                      <th className="text-left p-2 border-b">Projectonderdeel</th>
                      <th className="text-left p-2 border-b">Bedrag</th>
                      <th className="text-left p-2 border-b">Omschrijving</th>
                      <th className="text-left p-2 border-b w-20">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {splitLines.map((line, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2 align-top">
                          <Autocomplete
                            size="sm"
                            aria-label="Selecteer project"
                            placeholder="Zoek project..."
                            selectedKey={line.projectId ? line.projectId.toString() : null}
                            onSelectionChange={(key) =>
                              handleProjectChange(index, key ? Number(key) : 0)
                            }
                            popoverProps={{ className: "z-9999" }}
                          >
                            {availableProjects.map(project => (
                              <AutocompleteItem
                                key={project.id.toString()}
                                textValue={`RENO-${project.id}${project.title ? ` - ${project.title}` : ""}`}
                              >
                                RENO-{project.id}
                                {project.title ? ` - ${project.title}` : ""}
                              </AutocompleteItem>
                            ))}
                          </Autocomplete>
                        </td>
                        <td className="p-2 align-top">
                          <Autocomplete
                            size="sm"
                            aria-label="Selecteer projectonderdeel"
                            placeholder={line.projectId ? "Zoek onderdeel..." : "Kies eerst een project"}
                            isDisabled={!line.projectId}
                            selectedKey={line.partId ? line.partId.toString() : null}
                            onSelectionChange={(key) =>
                              handlePartChange(index, key ? Number(key) : 0)
                            }
                            popoverProps={{ className: "z-9999" }}
                          >
                            {getPartsForProject(line.projectId).map(part => (
                              <AutocompleteItem
                                key={part.id.toString()}
                                textValue={part.title}
                              >
                                {part.title}
                              </AutocompleteItem>
                            ))}
                          </Autocomplete>
                        </td>
                        <td className="p-2">
                          <div className="relative flex items-center">
                            <span className="absolute left-2 text-neutral-500">€</span>
                            <input
                              type="text"
                              className="w-full p-2 pl-6 border rounded-sm text-sm"
                              value={getDisplayAmount(index, line.amount)}
                              onChange={(e) => handleAmountChange(index, e.target.value)}
                              onFocus={() => handleAmountFocus(index)}
                              onBlur={() => handleAmountBlur(index)}
                            />
                          </div>
                        </td>
                        <td className="p-2">
                          <textarea
                            className={`w-full p-2 border rounded-sm text-sm resize-none ${!line.description || line.description.trim() === '' ? 'border-brick-primary' : ''}`}
                            value={line.description || ''}
                            onChange={(e) => {
                              const newLines = [...splitLines];
                              newLines[index] = {
                                ...newLines[index],
                                description: e.target.value
                              };
                              setSplitLines(newLines);
                            }}
                            placeholder="Omschrijving (verplicht)"
                            rows={2}
                            style={{ minHeight: '60px' }}
                          />
                        </td>
                        <td className="p-2">
                          <button
                            className="p-2 bg-brick-primary text-white rounded-md flex items-center justify-center"
                            onClick={() => handleRemoveLine(index)}
                          >
                            <Trash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="solid"
                  color="primary"
                  startContent={<Plus size={16} />}
                  onPress={handleAddLine}
                  className="bg-moss-primary text-white"
                >
                  Voeg regel toe
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            color="danger" 
            variant="light" 
            onPress={onClose}
            className="text-brick-primary"
          >
            Annuleren
          </Button>
          <Button 
            color="primary"
            variant="solid"
            onPress={handleSave}
            className="bg-moss-primary text-white"
            isDisabled={splitLines.some(line => !line.projectId || line.partId === 0) || splitLines.some(line => !line.description || line.description.trim() === '')}
          >
            Opslaan
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SplitInvoiceModal; 